import { useState, useEffect } from 'react';
import {
  getAllDDQTemplates, getAllFunds, publishDDQTemplate, unpublishDDQTemplate,
} from '../../../services/firestoreService';
import { useAuth } from '../../../hooks/useAuth';
import Table from '../../shared/UI/Table';
import Button from '../../shared/UI/Button';
import Badge from '../../shared/UI/Badge';

export default function DDQList({ onAdd, onEdit, typeFilter }) {
  const { user } = useAuth();
  const [allTemplates, setAllTemplates] = useState([]);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  function load() {
    setLoading(true);
    Promise.all([getAllDDQTemplates(), getAllFunds()])
      .then(([t, f]) => { setAllTemplates(t); setFunds(f); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  const templates = typeFilter
    ? allTemplates.filter(t =>
        typeFilter === 'dsq'
          ? t.template_type === 'dsq'
          : (t.template_type === 'ddq' || !t.template_type)
      )
    : allTemplates;

  function fundName(fid) {
    return funds.find(f => f.id === fid)?.name || fid || '—';
  }

  async function handlePublish(row) {
    const label = (row.template_type || 'ddq').toUpperCase();
    if (!window.confirm(
      `Publish "${row.name}"?\n\nAny other published ${label} template for the same ` +
      `fund and stream will be set back to Draft.`
    )) return;
    setBusyId(row.id);
    setError(null);
    try {
      await publishDDQTemplate({ templateId: row.id, publishedBy: user.uid });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  async function handleUnpublish(row) {
    if (!window.confirm(
      `Unpublish "${row.name}"?\n\nCompanies will no longer be able to load it.`
    )) return;
    setBusyId(row.id);
    setError(null);
    try {
      await unpublishDDQTemplate({ templateId: row.id, unpublishedBy: user.uid });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  const columns = [
    { key: 'name', label: 'Template Name' },
    { key: 'fund_id', label: 'Fund', render: row => fundName(row.fund_id) },
    { key: 'stream_id', label: 'Stream', render: row => row.stream_id || 'Fund Level' },
    {
      key: 'status',
      label: 'Status',
      // Templates created before publishing existed have no status field —
      // they are Draft (and invisible to companies) until an admin publishes.
      render: row => (
        <Badge text={row.status === 'published' ? 'Published' : 'Draft'} />
      ),
    },
    { key: 'version', label: 'Version', render: row => `v${row.version || 1}` },
    {
      key: 'created_at',
      label: 'Last Modified',
      render: row => row.created_at?.toDate ? row.created_at.toDate().toLocaleDateString() : '—',
    },
    {
      key: 'sections',
      label: 'Sections',
      render: row => (row.sections || []).length,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: row => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="secondary" onClick={() => onEdit(row)}>Edit</Button>
          {row.status === 'published' ? (
            <Button size="sm" variant="ghost" loading={busyId === row.id}
              onClick={() => handleUnpublish(row)}>Unpublish</Button>
          ) : (
            <Button size="sm" variant="primary" loading={busyId === row.id}
              onClick={() => handlePublish(row)}>Publish</Button>
          )}
        </div>
      ),
    },
  ];

  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <div className="card">
      <div className="card-header">
        <h2>{typeFilter === 'dsq' ? 'DSQ' : 'DDQ'} Templates ({templates.length})</h2>
        <Button variant="primary" onClick={onAdd}>+ Add New Template</Button>
      </div>
      <Table
        columns={columns}
        data={templates}
        loading={loading}
        emptyTitle="No DDQ templates configured"
        emptyMessage="Create a DDQ template to use in the screening stage."
        emptyAction={{ label: '+ Add Template', onClick: onAdd }}
      />
    </div>
  );
}
